import type {
  HeroPreloadAsset,
  LoadingVeilVariant,
  LoadingVeilVariantConfig,
} from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/loading-veil/types';
import {
  SECTION_VIDEO_LOAD_TIMEOUT_SECONDS,
  VIDEO_COVER_FALLBACK_SECONDS,
} from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/constants';

export const HERO_PRELOAD_ASSETS = [
  // The still behind every slide.
  'background',
  // The engine artwork the whole sequence is built around.
  'engine',
  // The DDi wordmark that animates in once the engine settles.
  'logo',
  // The clip's poster frame, which is what the first frame actually is.
  'videoCover',
  // The intro clip itself, resident or near-fully buffered.
  'introVideo',
] as const;

// The same cut-off every slide's clip gets, counted from the same moment
// -- page load -- so the first scene never waits longer than the others.
export const HERO_PRELOAD_TIMEOUT_MS =
  SECTION_VIDEO_LOAD_TIMEOUT_SECONDS * 1000;

// WAITED ON, BUT NOT FOR LONG. Once every image is in, the intro clip gets
// this much longer before the veil lifts without it -- onto its poster,
// which is the same picture. On a slow line that is a still frame instead
// of a black screen; the clip takes over whenever it arrives.
export const HERO_PRELOAD_GRACED_ASSET: HeroPreloadAsset = 'introVideo';
export const HERO_PRELOAD_GRACE_MS = VIDEO_COVER_FALLBACK_SECONDS * 1000;

export const LOADING_VEIL_FADE_OUT_SECONDS = 0.4;

export const LOADING_VEIL_VARIANTS: Record<
  LoadingVeilVariant,
  LoadingVeilVariantConfig
> = {
  // The first load. Solid black, over everything -- the intro clip (z-60)
  // included -- so the images never pop in one by one behind it.
  firstLoad: {
    className: 'z-60',
    isRemovedOnceHidden: true,
    // Server-rendered, so it is up before the bundle is. Without a CSS
    // cut-off a slow download or hydration would hold it past the timeout.
    cssCutoffMs: HERO_PRELOAD_TIMEOUT_MS,
  },
  // A slide waiting on its clip. Half black, so its cover image stays
  // visible underneath, and under the skip button (z-20), which stays the
  // way out.
  clip: {
    className: 'z-10 bg-black/50',
    isRemovedOnceHidden: false,
    cssCutoffMs: null,
  },
};
